/* @flow */
import cx from 'classnames';
import React from 'react';

import BlockMemberMenuItem from 'users/components/BlockMemberMenuItem.jsx';
import Button from 'components/Button.jsx';
import Chat from 'entities/Chat';
import ChatActions from 'chat/actions/ChatActions';
import ChatMember from 'entities/ChatMember';
import ChatMembership from 'entities/ChatMembership';
import ConfirmDialog from 'components/ConfirmDialog.jsx';
import CurrentUser from 'auth/CurrentUser';
import Avatar from 'components/Avatar.jsx';
import Glyph from 'components/Glyph.jsx';
import GroupStore from 'groups/stores/GroupStore';
import LayerTrigger from 'components/LayerTrigger.jsx';
import Link from 'components/Link.jsx';
import LoadingIndicator from 'components/LoadingIndicator.jsx';
import Menu from 'components/Menu.jsx';
import MenuButton from 'components/MenuButton.jsx';
import MenuItem from 'components/MenuItem.jsx';
import ModalMenuItem from 'components/ModalMenuItem.jsx';
import ModalTrigger from 'components/ModalTrigger.jsx';
import ActivityShareModal from 'payments/components/ActivityShareModal.jsx';
import RelationshipModal from 'user/components/RelationshipModal.jsx';
import StatusLabel from 'components/StatusLabel.jsx';
import StartChatButton from 'user/components/StartChatButton.jsx';
import Text from 'components/Text.jsx';
import ToastActions from 'actions/ToastActions';
import UserLink from 'users/components/UserLink.jsx';
import ChatAddParticipantContainer from 'chat/containers/ChatAddParticipantContainer.jsx';

import ReportChatSelectModal from './ReportChatSelectModal.jsx';

import './styles/ConversationSidebar.scss';

// May be omitted if the chat was loaded by v2/chats page
function hasMemberships(chat: Chat) {
  return chat.getMemberships().length > 0;
}

type Props = {
  chat: Chat,
};

class ConversationSidebarParticipants extends React.Component {
  props: Props;

  state = {
    showLeftMembers: false,
    hoverOwner: false,
    loading: [],
    missingMemberships: !hasMemberships(this.props.chat),
  };

  componentDidMount() {
    if (this.state.missingMemberships) {
      // triggers a props update
      ChatActions.loadChat(this.props.chat.id);
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    const nextMissingMemberships = !hasMemberships(nextProps.chat);
    if (nextMissingMemberships) {
      this.setState({ missingMemberships: true });
      // triggers a props update
      ChatActions.loadChat(this.props.chat.id);
    } else {
      this.setState({ missingMemberships: false });
    }
  }

  getChatTypeName(): string {
    return this.props.chat.isActivityType() ? 'activity' : 'conversation';
  }

  setHoverOwnerTo(value: boolean): void {
    this.setState({ hoverOwner: value });
  }

  addMemberToChat(member: ChatMember): void {
    this.showLoadingIndicatorFor(member);

    ChatActions.addMembersToChat(this.props.chat, [member.id]).then(() => {
      this.hideLoadingIndicatorFor(member);
      ToastActions.showSuccessToast(
        `${member.getName()} has been added back to the ${this.getChatTypeName()}`
      );
    });
  }

  hideLoadingIndicatorFor(member: ChatMember): void {
    const index = this.state.loading.indexOf(member.id);
    if (index > -1) {
      delete this.state.loading[index];
      this.forceUpdate();
    }
  }

  memberHasLoaded(member: ChatMember): boolean {
    return this.state.loading.indexOf(member.id) > -1;
  }

  removeMembershipFromChat(member: ChatMember): void {
    this.showLoadingIndicatorFor(member);

    ChatActions.changeMemberStatus(this.props.chat, member.id, 'removed').then(() => {
      this.hideLoadingIndicatorFor(member);
      ToastActions.showSuccessToast(
        `${member.getName()} has been removed from the ${this.getChatTypeName()}`
      );
    });
  }

  showLoadingIndicatorFor(member: ChatMember): void {
    this.state.loading.push(member.id);
    this.forceUpdate();
  }

  toggleShowLeftMembers = () => {
    this.setState(({ showLeftMembers }) => ({ showLeftMembers: !showLeftMembers }));
  }

  renderActiveParticipants() {
    const { chat } = this.props;

    return chat.getOtherChatMembershipsWithState('present')
      .sort(membership => !membership.isCreator(chat))
      .map(membership => this.renderParticipant(chat, membership));
  }

  renderShareActivity() {
    const { chat } = this.props;
    if (chat.isActivityType()) {
      const modal = <ActivityShareModal activity={chat.getActivity()} />;
      const button = (
        <div className="ConversationSidebar-item">
          <ModalTrigger modal={modal}>
            <Button
              className="ConversationSidebar-item-button"
              level="text"
              weight="semibold"
              eventElementName="ConversationSidebar-item-button">
              <Glyph name="plus-circle-fill" size="large" />
              <span>Share activity</span>
            </Button>
          </ModalTrigger>
        </div>
      );
      return button;
    }
    return null;
  }

  renderAddParticipants() {
    const { chat } = this.props;

    return (
      <ChatAddParticipantContainer chatUuid={chat.id} data-test="chat-add-participant-container">
        {(disabled) => {
          return (
            <Button
              level="text"
              className="ConversationSidebar-item-button"
              disabled={disabled}
              weight="semibold"
              eventElementName="ConversationSidebar-item-button">
              <Glyph name="plus-circle-fill" size="large" />
              <span>Add people</span>
            </Button>
          );
        }}
      </ChatAddParticipantContainer>
    );
  }

  renderGroupOwner(menu, member: ChatMember) {
    const button = this.renderGroupOwnerButton(menu);

    return (
      <div
        key={member.id}
        className={cx('ConversationSidebar-participant', 'ConversationSidebar-participant-present')}
        onMouseLeave={() => this.setHoverOwnerTo(false)}
        onMouseEnter={() => this.setHoverOwnerTo(true)}>
        {this.renderParticipantNameTag(member)}
        {button}
      </div>
    );
  }

  renderGroupOwnerButton(menu) {
    let body = <StatusLabel className="ConversationSidebar-participant-label">Owner</StatusLabel>;

    if (this.state.hoverOwner) {
      body = (
        <Button
          level="secondary"
          size="xsmall"
          className="ConversationSidebar-participant-menuButton"
          eventElementName="ConversationSidebar-participant-menuButton">
          <Glyph size="large" name="ellipses" solo />
        </Button>
      );
    }

    return (
      <LayerTrigger layer={menu} alignment="right" placement="bottom" trigger="click">
        {body}
      </LayerTrigger>
    );
  }

  renderLeftParticipants() {
    const { chat } = this.props;
    const leftMembership = chat.getOtherChatMembershipsWithState('left');
    const removedMembership = chat.getOtherChatMembershipsWithState('removed');
    const total = leftMembership.length + removedMembership.length;

    if (total > 0) {
      let glyph;

      let leftList;
      let removedList;

      if (this.state.showLeftMembers) {
        leftList = leftMembership.map(membership => {
          return this.renderParticipant(chat, membership);
        });

        removedList = removedMembership.map(membership => {
          return this.renderParticipant(chat, membership);
        });

        glyph = <Glyph name="chevron-up" size="xsmall" />;
      } else {
        glyph = <Glyph name="chevron-down" size="xsmall" />;
      }

      const header = total === 1 ?
        `1 person left this ${this.getChatTypeName()}` :
        `${total} people left this ${this.getChatTypeName()}`;

      return (
        <div className="ConversationSidebar-participantsleft">
          <div className="ConversationSidebar-participantsleft-header">
            <Link onClick={this.toggleShowLeftMembers}>
              <span>
                {header}
              </span>
              {glyph}
            </Link>
          </div>
          {leftList}
          {removedList}
        </div>
      );
    }

    return null;
  }

  renderMeParticipant() {
    const { chat } = this.props;

    if (chat.isIndividualType()) {
      return null;
    }

    let owner;
    if (chat.getCreatorId() === CurrentUser.getUserId()) {
      owner = (
        <StatusLabel className="ConversationSidebar-tag-me">
          Owner
        </StatusLabel>
      );
    }

    return (
      <div className="ConversationSidebar-participant ConversationSidebar-participant-present">
        You
        {owner}
      </div>
    );
  }

  renderMenuForParticipant(chat: Chat, membership: ChatMembership) {
    const member = membership.getMember();
    const ownedGroups = GroupStore.getOwnedGroups();

    const reportModal = (
      <ReportChatSelectModal
        chat={chat}
        partner={member}
      />
    );

    let addToChat;
    if (chat.canAdd() && (membership.hasLeft() || membership.hasBeenRemoved())) {
      addToChat = (
        <MenuItem
          menuKey={member.id}
          disabled={chat.hasReachedParticipantNumberLimit()}
          onClick={() => this.addMemberToChat(member)}>
          <Glyph name="plus-circle-fill" size="medium" className="mrm" />
          <span>
            Add back to this {this.getChatTypeName()}
          </span>
        </MenuItem>
      );
    }

    let removeFromChat;
    if (membership.canRemove()) {
      removeFromChat = (
        <ModalMenuItem
          modal={this.renderRemoveFromChatConfirmModal(member)}
          className="ConversationSidebar-participant-menuItem-negative">
          <Glyph name="close" size="medium" className="mrm" />
          <span data-test-remove-participant>
            Remove from this {this.getChatTypeName()}
          </span>
        </ModalMenuItem>
      );
    }

    let removeFromClass;
    if (ownedGroups.some(group => chat.isInGroup(group.id))) {
      removeFromClass = (
        <ModalMenuItem
          modal={this.renderRelationshipModal(member.id)}
          disabled={chat.isUnsubscribed()}>
          <Glyph name="close" size="medium" className="mrm" />
          <span>
            Remove from class…
          </span>
        </ModalMenuItem>
      );
    }

    let individualMessageStarter;
    if (member.isEligibleForChat()) {
      individualMessageStarter = (
        <MenuItem menuKey={member.id}>
          <StartChatButton subscriber={member}>
            <Glyph name="chat-add-bubble" size="medium" className="mrm" />
            <span>
              Send message
            </span>
          </StartChatButton>
        </MenuItem>
      );
    }

    // FIXME: Remove spans required for React 15 upgrade
    return (
      <Menu>
        <ModalMenuItem
          modal={this.renderRelationshipModal(member.id)}
          disabled={chat.isUnsubscribed()}>
          <Glyph name="avatar" size="medium" className="mrm" />
          <span>
            View profile
          </span>
        </ModalMenuItem>
        {addToChat}
        {removeFromChat}
        {removeFromClass}
        {individualMessageStarter}
        <BlockMemberMenuItem
          blocked={member.isBlocked()}
          memberId={member.getUserId()}
          memberName={member.getName()}
        />
        <ModalMenuItem
          modal={reportModal}
          className="ConversationSidebar-participant-menuItem-negative">
          <Glyph name="flag" size="medium" className="mrm" />
          <span>
            Report to Remind
          </span>
        </ModalMenuItem>
      </Menu>
    );
  }

  renderParticipant(chat: Chat, membership: ChatMembership) {
    const member = membership.getMember();
    const menu = this.renderMenuForParticipant(chat, membership);

    // if this is the owner, render the special "owner" tag.
    if (chat.getCreatorId() === member.id) {
      return this.renderGroupOwner(menu, member);
    }

    let text = <Glyph size="large" name="ellipses" solo />;
    if (this.memberHasLoaded(member)) {
      text = <LoadingIndicator size="xsmall" />;
    }

    // to apply diff styling if you are present or not
    // (owner is always present).
    const presentClass = membership.isPresent() ?
      'ConversationSidebar-participant-present' :
      'ConversationSidebar-participant-notpresent';

    return (
      <div
        key={member.id}
        className={cx('ConversationSidebar-participant', presentClass)}>
        {this.renderParticipantNameTag(member)}
        <MenuButton
          menu={menu}
          className="ConversationSidebar-participant-menuButton"
          activeClassName="ConversationSidebar-participant-menuButton--active"
          level="icon"
          size="xsmall"
          alignment="right">
          {text}
        </MenuButton>
      </div>
    );
  }

  renderParticipantNameTag(member: ChatMember) {
    return (
      <ModalTrigger
        key={member.id}
        modal={this.renderRelationshipModal(member.id)}>
        <Text
          className="ConversationSidebar-participant-info"
          truncate>
          <UserLink
            className="ConversationSidebar-participant-name"
            userId={member.id}
            placement="left"
            alignment="top">
            <Avatar
              className="ConversationSidebar-droplet"
              colorHex={member.getColor()}
              text={member.getInitials()}
              size="small"
            />
            {member.getName()}
          </UserLink>
        </Text>
      </ModalTrigger>
    );
  }

  renderRelationshipModal(userId: number) {
    return <RelationshipModal userId={userId} />;
  }

  renderRemoveFromChatConfirmModal(member: ChatMember) {
    return (
      <ConfirmDialog
        eventScreenName="ConversationSidebarParticipants"
        title={`Remove ${member.getName()} from the conversation?`}
        actionButtonText="Remove"
        cancelButtonText="Cancel"
        onConfirm={() => this.removeMembershipFromChat(member)}
        subtext={
          `${member.getName()} won’t be able to view
          or send messages to this ${this.getChatTypeName()}.`
        }
      />
    );
  }

  render() {
    if (this.state.missingMemberships) {
      return <LoadingIndicator size="xsmall" className="ConversationSidebar-participants-loading" />;
    }

    return (
      <div>
        {this.renderAddParticipants()}
        {this.renderShareActivity()}
        {this.renderMeParticipant()}
        {this.renderActiveParticipants()}
        {this.renderLeftParticipants()}
      </div>
    );
  }
}

export default ConversationSidebarParticipants;



// WEBPACK FOOTER //
// ./src/chat/components/ConversationSidebarParticipants.jsx